import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@cieda/djayma-ui";

interface Props {
  readonly children: ReactNode;
  readonly fallbackTitle?: string;
  readonly fallbackMessage?: string;
  readonly onReset?: () => void;
}

interface State {
  readonly hasError: boolean;
  readonly error: Error | null;
}

/**
 * Error boundary that wraps the admin Products page. Catches rendering errors
 * in any descendant component, logs to Sentry if available, and surfaces a
 * retry action. Retry resets internal state; callers may pass `onReset` to
 * additionally invalidate queries or reset parent state.
 *
 * Per React docs, class components are still required for error boundaries —
 * there is no hook equivalent as of React 19.
 */
export class ProductsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Forward to Sentry if the global is available (client-side only).
    if (typeof window !== "undefined") {
      const sentry = (
        window as unknown as {
          Sentry?: {
            captureException?: (
              err: Error,
              ctx?: Record<string, unknown>,
            ) => void;
          };
        }
      ).Sentry;
      sentry?.captureException?.(error, { extra: { componentStack: info.componentStack } });
    }
    // Surface in dev console to aid debugging
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error("ProductsErrorBoundary caught:", error, info);
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-8 text-center"
          role="alert"
        >
          <AlertCircle
            className="h-8 w-8 text-red-500"
            aria-hidden="true"
          />
          <div>
            <h2 className="text-lg font-semibold text-stone-900">
              {this.props.fallbackTitle ?? "Erreur inattendue"}
            </h2>
            <p className="mt-1 text-sm text-stone-600">
              {this.props.fallbackMessage ??
                "Un problème est survenu lors du chargement des produits."}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={this.handleRetry}
            type="button"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Réessayer
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
